import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'use-intl';
import useFormat from '@/hooks/useFormat';
import useAuthDrawer from '@/components/organisms/authentication/auth-drawer-provider/hooks/useAuthDrawer';
import { SIGN_IN_DRAWER_ID } from '@/components/organisms/authentication/sign-in-drawer';
import { PriceBreak, Product } from '@/types/entity/product';
import { classnames } from '@/utils/classnames/classnames';

interface PricingCardProps {
  product: Pick<Product, 'price' | 'discountedPrice' | 'currency' | 'rrp' | 'priceBreaks'>;
  isGuest?: boolean;
  // CT enrichment couldn't surface a dealer price for the active variant.
  // Standalone Prices on the dealer's channel are the only authoritative
  // source — embedded variant prices (which may be the public RRP) are
  // intentionally ignored. When true, the card replaces the price + bulk
  // pricing + RRP pill with an "Unavailable" label; the quote CTA stays.
  priceUnavailable?: boolean;
  onRequestQuote: () => void;
  className?: string;
}

const BulkPricingTable = ({ priceBreaks, currency }: { priceBreaks: PriceBreak[]; currency: Product['currency'] }) => {
  const translate = useTranslations();
  const { formatCurrency } = useFormat();
  const headerCellClass = 'text-11 font-semibold tracking-wide text-gray-600 uppercase';

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* EWM-277 — on `< md` viewports, bold currency labels crush the grid.
          Wrap the table in a horizontal-scroll container with a min-width so
          the columns stay readable and dealers can thumb-scroll. Desktop
          unchanged. */}
      <div className="overflow-x-auto md:overflow-visible">
        <div className="min-w-[360px] md:min-w-0">
          {/* EWM-316 — the Unit/Carton/Pallet column is dropped for all
              products and customer types; Quantity + Price fill the width. */}
          <div className="grid grid-cols-2 border-b border-gray-200 bg-gray-100 px-3 py-2">
            <span className={classnames(headerCellClass, 'text-center')}>{translate('pdp.bulk-pricing-quantity')}</span>
            <span className={classnames(headerCellClass, 'text-right')}>{translate('pdp.bulk-pricing-price')}</span>
          </div>
          {priceBreaks.map((tier, idx) => (
            <div
              key={`tier-${tier.quantity}-${idx}`}
              // EWM-272 — Dan v0 omits the inter-row separator (white rows
              // with no border, distinguishing tiers by spacing only).
              // The previous `border-b border-gray-100` was a project
              // default; removed for visual parity with the mock.
              className="grid grid-cols-2 items-center px-3 py-2.5"
            >
              <span className="text-13 text-center text-gray-700">{tier.quantity}</span>
              <span className="text-13 text-primary text-right font-bold">{formatCurrency(tier.price, currency)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PricingCard = ({
  product,
  isGuest = false,
  priceUnavailable = false,
  onRequestQuote,
  className,
}: PricingCardProps) => {
  const translate = useTranslations();
  const { formatCurrency } = useFormat();
  const { mode: authMode, open: openAuthDrawer } = useAuthDrawer();
  const [bulkOpen, setBulkOpen] = useState(false);

  // EWM-195 — Guest pricing path: show the RRP and a trade-pricing
  // login CTA. Guests resolve their price via the BFF `guestFallback`
  // path (embedded RRP from the `price-rrp` channel), so `product.price`
  // is the RRP for them. Wins over `priceUnavailable` because the
  // login CTA is the actionable outcome for a guest — "Unavailable"
  // would be a dead-end. Falls back to `product.rrp` if it's set
  // (defensive — both fields will normally hold the RRP for guests).
  if (isGuest) {
    const guestPrice = product.rrp ?? product.price;
    return (
      <div
        className={classnames('flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 pr-5', className)}
        data-testid="pricing-card"
      >
        {guestPrice > 0 ? (
          <p className="text-primary text-24 font-bold">{formatCurrency(guestPrice, product.currency)}</p>
        ) : (
          <p className="text-24 font-bold text-gray-500">{translate('product.price-unavailable')}</p>
        )}
        {/* Sign-in opens the app-wide AuthDrawer, not a `/login` route — that
            path isn't a published page and 404s (EWM-385). */}
        <button
          type="button"
          onClick={() => openAuthDrawer('sign-in')}
          aria-haspopup="dialog"
          aria-controls={SIGN_IN_DRAWER_ID}
          aria-expanded={authMode === 'sign-in'}
          className="text-primary text-13 self-start font-semibold hover:underline"
        >
          {translate('pdp.login-for-trade-pricing')}
        </button>
      </div>
    );
  }

  // CT enrichment returned no price for this variant — render the
  // "Unavailable" label and the quote CTA. Mirrors the PLP tile's
  // priceUnavailable treatment. Reached only for logged-in dealers
  // (guests are handled above).
  if (priceUnavailable) {
    return (
      <div
        className={classnames('flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 pr-5', className)}
        data-testid="pricing-card"
      >
        <p className="text-24 font-bold text-gray-500">{translate('product.price-unavailable')}</p>
        {/* EWM-592 — align the "Request a Quote" link to the top so it lines up
            with the title row, not floated against the centre of the two-line
            title+description block. */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-primary text-13 mb-0.5 font-bold">{translate('pdp.quote-cta-title')}</h3>
            <p className="text-12 text-gray-600">{translate('pdp.quote-cta-description')}</p>
          </div>
          <button
            type="button"
            onClick={onRequestQuote}
            className="text-primary text-13 shrink-0 leading-snug font-bold whitespace-nowrap hover:underline"
          >
            {translate('pdp.quote-request-link')}
          </button>
        </div>
      </div>
    );
  }

  // EWM-598 — was/now display. `product.discountedPrice` is the
  // product-discounted ("now") price the customer is charged (CT applies it);
  // `product.price` is the original ("was"). Mirrors the PLP tile's
  // `discountedPrice < price` test so PDP + PLP stay consistent.
  const hasDiscount =
    product.discountedPrice != null && product.discountedPrice > 0 && product.discountedPrice < product.price;
  const currentPrice = hasDiscount ? (product.discountedPrice as number) : product.price;
  const hasBulkPricing = product.priceBreaks && product.priceBreaks.length > 0;
  const hasRrp = product.rrp != null && product.rrp > currentPrice;

  // EWM-598 — saving label. Percentage when the saving is a clean share of the
  // original (round to the nearest whole %), absolute $ amount otherwise.
  // Dan's mock shows the saving in a clear accent next to the was/now prices.
  const savingAmount = hasDiscount ? product.price - currentPrice : 0;
  const savingPercent = hasDiscount ? Math.round((savingAmount / product.price) * 100) : 0;
  const savingLabel = hasDiscount
    ? savingPercent > 0
      ? translate('pdp.percent-off', { percent: savingPercent })
      : translate('pdp.save-amount', { amount: formatCurrency(savingAmount, product.currency) })
    : undefined;

  return (
    <div
      className={classnames('rounded-lg border border-gray-200 bg-gray-50 p-4 pr-5', className)}
      data-testid="pricing-card"
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-primary text-24 font-bold">{formatCurrency(currentPrice, product.currency)}</span>
          {hasDiscount && (
            <>
              {/* `<s>` marks the original as no-longer-current; the visually
                  hidden label keeps screen readers from reading it as the
                  price to pay. */}
              <s className="text-14 text-gray-400">
                <span className="sr-only">{translate('pdp.was-price')} </span>
                {formatCurrency(product.price, product.currency)}
              </s>
              {savingLabel && (
                <span className="text-13 font-semibold text-red-600" data-testid="pricing-card-saving">
                  {savingLabel}
                </span>
              )}
            </>
          )}
        </div>
        {hasRrp && (
          <span className="text-14 border-primary text-primary inline-block rounded-full border-2 bg-white px-4 py-1.5 font-semibold">
            {translate('pdp.rrp')} {formatCurrency(product.rrp as number, product.currency)}
          </span>
        )}
      </div>

      {hasBulkPricing && (
        <div className="border-t border-gray-300 py-4">
          <button
            type="button"
            onClick={() => setBulkOpen((open) => !open)}
            aria-expanded={bulkOpen}
            aria-controls="pricing-card-bulk"
            className="flex w-full items-center justify-between text-left"
          >
            <span className="text-primary text-14 font-bold">{translate('pdp.bulk-pricing-title')}</span>
            {/* EWM-480 — arrow up when collapsed, down when expanded. */}
            {bulkOpen ? (
              <ChevronDownIcon aria-hidden className="text-primary size-5 shrink-0" />
            ) : (
              <ChevronUpIcon aria-hidden className="text-primary size-5 shrink-0" />
            )}
          </button>
          {bulkOpen && (
            <div id="pricing-card-bulk" className="mt-4">
              <BulkPricingTable priceBreaks={product.priceBreaks ?? []} currency={product.currency} />
            </div>
          )}
        </div>
      )}

      {/* EWM-592 — top-align the link with the title row (see note above).
          EWM-619 — a divider separates the price/bulk area from the
          request-a-quote row (matches Dan's mock). QA re-test: `gray-200` was
          too faint to read on the `gray-50` card, so the internal dividers use
          `gray-300` for a clearly visible separation (kept in step with the
          bulk-pricing divider above). */}
      <div className="flex items-start justify-between gap-4 border-t border-gray-300 pt-4">
        <div>
          <h3 className="text-primary text-13 mb-0.5 font-bold">{translate('pdp.quote-cta-title')}</h3>
          <p className="text-12 text-gray-600">{translate('pdp.quote-cta-description')}</p>
        </div>
        <button
          type="button"
          onClick={onRequestQuote}
          className="text-primary text-13 shrink-0 leading-snug font-bold whitespace-nowrap hover:underline"
        >
          {translate('pdp.quote-request-link')}
        </button>
      </div>
    </div>
  );
};

export default PricingCard;
